import React, { useState, useCallback, ReactNode, useContext } from 'react';
import { MessageType } from './messageTypes';
import { v4 as uuidv4 } from 'uuid';
import MessageContext from './MessageContext';
import MessageContextType from './MessageContextType';
import { MessageBase } from './messageBase';
import { WithTranslationProps } from '../Translation/WithTranslationProps';
import withTranslation from 'Common/withTranslation';
import { MessagesIconClass, MessagesIcons } from './MessagesColors';

export const useMessages = () => {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
};

const MessageProvider: React.FC<{ children: ReactNode } & WithTranslationProps> = ({ children, t }) => {
  const [messages, setMessagesContext] = useState<MessageBase[]>([]);
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>(() => () => { });

  const removeMessage = useCallback((id: string) => {
    setMessagesContext(currentMessages => currentMessages.filter(message => message.id !== id));
  }, []);

  const showMessage: MessageContextType['showMessage'] = (messageOrMessage: MessageBase | string, type?: MessageType, title?: string | null) => {
    return new Promise<boolean>((resolve) => {
      let message: MessageBase;
      if (typeof messageOrMessage === 'string') {
        const id = uuidv4();
        message = {
          id,
          message: messageOrMessage, // Apply translation to the message
          type: type || MessageType.Success,
          title: title ? title : '', // Apply translation to the title
        };
      } else {
        message = { ...messageOrMessage, message: messageOrMessage.message }; // Translate the message if it's a MessageBase object
      }

      setMessagesContext(prevMessages => [...prevMessages, message]);

      if (message.type !== MessageType.Question) {
        resolve(false);
      } else {
        setResolvePromise(() => (value: boolean) => {
          removeMessage(message.id);
          resolve(value);
        });
      }
    });
  };

  const handleYesClick = () => {
    resolvePromise(true);
  };

  const handleNoClick = () => {
    resolvePromise(false);
  };

  const setMessages: MessageContextType['setMessages'] = (messages?: MessageBase[]) => {
    if (messages) {
      messages.forEach(message => {
        showMessage(message);
      });
    }
  };

  return (
    <MessageContext.Provider value={{ showMessage, setMessages }}>
      {children}
      <div className="z-[9998] flex justify-center items-center">
        {messages.map(({ id, title, message, type }) => {
          const MessageIcon = MessagesIcons[type];
          const iconClass = MessagesIconClass[type];
          return (
            <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-50 p-2 min-h-screen" key={id}>
              <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[9997] bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="flex flex-col items-center p-6 text-center">
                  {MessageIcon && (
                    <div className="pt-2">
                      <MessageIcon size={50} className={`text-${iconClass}-500`} />
                    </div>
                  )}
                  {title && (
                    <h3 className="text-xl font-bold break-words">
                      {t(title)}
                    </h3>
                  )}
                  <div className="overflow-y-auto overflow-x-hidden max-h-[calc(100vh-12rem)] w-full break-words">
                    <p className="text-base leading-relaxed break-words">
                      {t(message)}
                    </p>
                  </div>
                  <div className="flex justify-center items-center gap-4 mt-4">
                    <button
                      className="bg-emerald-500 hover:bg-emerald-600 rounded-full text-xs text-white px-4 py-2 shadow hover:shadow-lg transition-all duration-150"
                      onClick={type === MessageType.Question ? () => handleYesClick() : () => removeMessage(id)}
                    >
                      <b>{type === MessageType.Question ? t('General.Yes') : t('General.Ok')}</b> {/* Translate buttons */}
                    </button>
                    {type === MessageType.Question && (
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white text-xs px-4 py-2 rounded-full shadow hover:shadow-lg transition-all duration-150"
                        onClick={handleNoClick}
                      >
                        <b>{t('General.No')}</b> {/* Translate No button */}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="opacity-50 fixed inset-0 z-[9996] bg-black"></div>
            </div>
          );
        }).reverse()}
      </div>
    </MessageContext.Provider>
  );
};

// Wrap MessageProvider with withTranslation HOC
export default withTranslation(MessageProvider);

// import React, { createContext, useState, useCallback, useContext, ReactNode } from 'react';
// import { MessageType } from './messageTypes';
// import { v4 as uuidv4 } from 'uuid';
// import MessageContextType from './MessageContextType';
// import { MessagesIcons, MessagesIconClass } from './MessagesColors';
// import { MessageBase } from './messageBase';
// import withTranslation from 'Common/withTranslation';

// const MessageContext = createContext<MessageContextType | undefined>(undefined);

// export const useMessages = () => {
//   const context = useContext(MessageContext);
//   if (context === undefined) {
//     throw new Error('useMessages must be used within a MessageProvider');
//   }
//   return context;
// };

// export const MessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
//   const [messages, setMessagesContext] = useState<MessageBase[]>([]);
//   const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>(() => () => {});

//   const removeMessage = useCallback((id: string) => {
//     setMessagesContext(currentMessages => currentMessages.filter(message => message.id !== id));
//   }, []);

//   const showMessage: MessageContextType["showMessage"] = (messageOrMessage: MessageBase | string, type?: MessageType, title?: string | null) => {
//     return new Promise<boolean>((resolve) => {
//       let message: MessageBase;
//       if (typeof messageOrMessage === 'string') {
//         const id = uuidv4();
//         message = { id, message: messageOrMessage, type: type || MessageType.Success, title: title || '' };
//       } else {
//         message = messageOrMessage;
//       }

//       setMessagesContext(prevMessages => [...prevMessages, message]);

//       if (message.type !== MessageType.Question) {
//         resolve(false);
//       } else {
//         setResolvePromise(() => (value: boolean) => {
//           removeMessage(message.id);
//           resolve(value);
//         });
//       }
//     });
//   };

//   const handleYesClick = () => {
//     resolvePromise(true);
//   };

//   const handleNoClick = () => {
//     resolvePromise(false);
//   };

//   const setMessages: MessageContextType["setMessages"] = (messages?: MessageBase[]) => {
//     if (messages) {
//       messages.forEach(message => {
//         showMessage(message);
//       });
//     }
//   };

//   return (
//     <MessageContext.Provider value={{ showMessage, setMessages }}>
//       {children}
//       <div className="z-[9998] flex justify-center items-center">
//         {messages.map(({ id, title, message, type }) => {
//           const MessageIcon = MessagesIcons[type];
//           const iconClass = MessagesIconClass[type];
//           return (
//             <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-50 p-2 min-h-screen" key={id}>
//               <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[9997] bg-white rounded-lg shadow-lg overflow-hidden">
//                 <div className="flex flex-col items-center p-6 text-center">
//                   {MessageIcon && (
//                     <div className="pt-2">
//                       <MessageIcon size={50} className={`text-${iconClass}-500`} />
//                     </div>
//                   )}
//                   {title && (
//                     <h3 className="text-xl font-bold break-words">
//                       {title}
//                     </h3>
//                   )}
//                   <div className="overflow-y-auto overflow-x-hidden max-h-[calc(100vh-12rem)] w-full break-words">
//                     <p className="text-base leading-relaxed break-words">
//                       {message}
//                     </p>
//                   </div>
//                   <div className="flex justify-center items-center gap-4 mt-4">
//                     <button
//                       className="bg-emerald-500 hover:bg-emerald-600 rounded-full text-xs text-white px-4 py-2 shadow hover:shadow-lg transition-all duration-150"
//                       onClick={type === MessageType.Question ? () => handleYesClick() : () => removeMessage(id)}
//                     >
//                       <b>{type === MessageType.Question ? 'Si' : 'Aceptar'}</b>
//                     </button>
//                     {type === MessageType.Question && (
//                       <button
//                         className="bg-red-500 hover:bg-red-600 text-white text-xs px-4 py-2 rounded-full shadow hover:shadow-lg transition-all duration-150"
//                         onClick={handleNoClick}
//                       >
//                         <b>No</b>
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               </div>
//               <div className="opacity-50 fixed inset-0 z-[9996] bg-black"></div>
//             </div>
//           );
//         }).reverse()}
//       </div>
//     </MessageContext.Provider>
//   );
// };

// export default MessageContext;